/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/assistantContextualPrompts.ts
 * PURPOSE: Route-aware starter prompts and follow-up chips so the Langflow assistant feels grounded
 *          like Perplexity (suggestions match the page the user is on).
 *
 * RECENT: 2026-06-22 [2.2.1470] — Manager team-scope starters for /crm/accounts?scope=team; dashboard persona starters on /.
 * RECENT_PRIOR: 2026-04-29 — **`/crm`** and **`/crm/*`** use dedicated CRM starters and follow-ups so paths like
 *          **`/crm/contacts`** do not substring-match **`/contacts`** or **`/deals`** first.
 */

import { readDashboardSalesPersona } from '../../../pages/dashboard/dashboardRoleViews';

export type AssistantRouteContext = {
  path: string;
  title?: string;
};

function normPath(ctx?: AssistantRouteContext | null): string {
  return (ctx?.path ?? '').toLowerCase();
}

/** Short label from document title for natural-language prompts (no HTML). */
export function assistantContextTitleSnippet(ctx?: AssistantRouteContext | null): string | null {
  const t = (ctx?.title ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  if (t.length <= 56) return t;
  return `${t.slice(0, 53)}…`;
}

const DEFAULT_STARTERS = [
  'What should I prioritize in my workspace today?',
  'Summarize pipeline health and next best actions.',
  'Draft a concise follow-up for my most important open deal.',
] as const;

const DEFAULT_FOLLOW_UPS = [
  'What are the main risks or gaps?',
  'Give me three bullet takeaways.',
  'What should I do next?',
] as const;

export function getAssistantStarterPrompts(ctx?: AssistantRouteContext | null): string[] {
  const p = normPath(ctx);
  const doc = assistantContextTitleSnippet(ctx);

  if (p.includes('/sales-hub')) {
    return [
      'What should I create or improve in Sales Hub based on what I am looking at?',
      'Give me three sharp enablement ideas aligned to this hub.',
      doc
        ? `Suggest next steps specifically for “${doc}”.`
        : 'What gaps usually show up between our materials and real buyer questions?',
    ];
  }

  /** My quota — rep self-service targets (before generic `/crm/` match) */
  if (p.includes('/crm/my-quota') || p.includes('/my-quota')) {
    return [
      'How am I tracking against my quota this quarter, and what should I prioritize?',
      'What pipeline or deals would close the gap to my target?',
      'If my quota is locked by CRM sync, what should I ask my manager?',
    ];
  }

  /** Territory structure — Sales Directors set revenue plans and review alignment */
  if (
    p.includes('/settings') &&
    (p.includes('tab=territories') || p.includes('territories'))
  ) {
    return [
      'Which territories have the largest quota alignment gap this quarter?',
      'How do territory revenue plans compare to rep quotas on my team?',
      'What territory plan changes happened recently that I should know about?',
    ];
  }

  /** Team quotas (managers) — before generic Settings */
  if (
    p.includes('/settings') &&
    (p.includes('tab=sales-targets') || p.includes('sales-targets'))
  ) {
    return [
      'Summarize my team quota roll-up: who is on track, at risk, and off track?',
      'Which reps on my team are behind quota and what coaching should I give?',
      'How do territory revenue plans compare to rep quotas on my team?',
    ];
  }

  if (p.includes('/settings')) {
    return [
      'How do territory revenue plans compare to rep quotas on my team?',
      'Which territories have the largest quota alignment gap this quarter?',
      'What territory plan changes happened recently that I should know about?',
    ];
  }

  /** My accounts portfolio — team patch (managers) */
  if (
    (p === '/crm/accounts' ||
      p === '/crm/territory' ||
      (p.startsWith('/crm/accounts') && !/\/crm\/accounts\/[^/]+/.test(p))) &&
    p.includes('scope=team')
  ) {
    return [
      'Which rep on my team has the riskiest accounts in this view, and what coaching should I give each?',
      'Summarize at-risk and renewal accounts across my team patch — who should act on each?',
      doc
        ? `Ground your answer in my team portfolio view: “${doc}”.`
        : 'What whitespace or expansion opportunities is my team missing in this patch?',
    ];
  }

  /** My accounts portfolio — field seller patch view (before generic `/crm/` match) */
  if (
    p === '/crm/accounts' ||
    p === '/crm/territory' ||
    (p.startsWith('/crm/accounts') && !/\/crm\/accounts\/[^/]+/.test(p))
  ) {
    return [
      'Which accounts in my patch need attention first this week, and why?',
      'Which accounts are stalled, expansion-ready, or high-fit but low-coverage in my patch, and why?',
      'Summarize at-risk and renewal accounts in my current filter — what is the play for each?',
      doc
        ? `Ground your answer in my portfolio view: “${doc}”.`
        : 'What whitespace or expansion opportunities am I missing in my account patch?',
    ];
  }

  /** CRM contacts list */
  if (p.startsWith('/crm/contacts/') && /\/crm\/contacts\/[^/]+/.test(p)) {
    return [
      doc
        ? `What should I do next with ${doc}, and what context am I missing?`
        : 'What is the best next touch for this contact based on their activity and deals?',
      'Draft a concise follow-up email I can send today.',
      'Which related deals or accounts should I review before my next call?',
    ];
  }

  /** CRM contacts list */
  if (p === '/crm/contacts') {
    return [
      'Who should I reach out to next from my contacts list, and with what angle?',
      'Which contacts have gone stale and need a re-engagement plan?',
      'Summarize my highest-priority contacts for this week.',
    ];
  }

  /** CRM shell (`/crm`, `/crm/deals`, …) — must run before `/deals` or `/contacts` substring checks */
  if (p === '/crm' || p.startsWith('/crm/')) {
    return [
      'Summarize what matters on this CRM view and the next three actions I should take.',
      doc
        ? `Ground suggestions in what I am seeing: “${doc}”.`
        : 'How do pipeline, territory, and contacts line up for me this week?',
      'Draft a concise weekly sales update I could send to my manager from this context.',
    ];
  }

  if (p.includes('/deals')) {
    return [
      doc
        ? `What should I do next on “${doc}”, and what am I missing?`
        : 'Which open deals need attention this week, and why?',
      'Draft a tight deal recap I can paste into CRM or email.',
      'What risks or blockers should I surface to leadership?',
    ];
  }

  if (p.includes('/pipeline-generation') || p.includes('/pipe')) {
    return [
      'How healthy is my pipe generation funnel, and what should I change?',
      'Suggest three experiments to improve conversion this month.',
      'What should I prioritize in outbound or inbound pipe gen today?',
    ];
  }

  if (p.includes('/calendar')) {
    return [
      'What should I prep for my upcoming meetings?',
      'Draft a concise agenda for my next external meeting.',
      'Flag conflicts, prep gaps, or follow-ups I might be missing.',
    ];
  }

  if (p.includes('/messaging')) {
    return [
      'Summarize threads that need a reply today.',
      'Draft a polite, firm follow-up I can send in one pass.',
      'What tone or clarity issues should I fix before I hit send?',
    ];
  }

  if (p.includes('/calls')) {
    return [
      'What should I review before my next call?',
      'Draft a short call plan: goal, questions, and next step.',
      'How do I handle objections I often hear on discovery calls?',
    ];
  }

  if (p.includes('/contacts')) {
    return [
      doc
        ? `What context should I gather before engaging “${doc}”?`
        : 'Who should I reach out to next, and with what angle?',
      'Draft a brief intro note that feels personal, not generic.',
      'What fields or relationships am I probably missing in my CRM view?',
    ];
  }

  if (p.includes('/territory')) {
    return [
      'Where are the whitespace and concentration risks in my territory?',
      'Suggest a weekly territory plan I can actually execute.',
      'What accounts deserve a deeper pass this quarter?',
    ];
  }

  if (p.includes('/langflow-assistant')) {
    return [
      'How many active deals and what is total pipeline value?',
      'Find contact Bob Smith',
      'What are the main risks or gaps in my pipeline?',
    ];
  }

  if (p.includes('/tenant-admin')) {
    return [
      'What tenant admin tasks are easy to miss but high impact?',
      'Summarize billing, seats, or usage I should watch this week.',
      'Draft a short internal note on settings or access changes.',
    ];
  }

  if (p.includes('/activities') || p.includes('/activity')) {
    return [
      'What should I tackle first in my activity feed?',
      'Summarize overdue or stale items I should close out.',
      'Draft a quick update I can send to my manager on progress.',
    ];
  }

  if (p === '/' || p.startsWith('/?') || p === '/home' || p === '/langflow-assistant' || p === '/assistant') {
    const salesPersona = readDashboardSalesPersona();
    if (salesPersona === 'manager') {
      return [
        'Which reps on my team need coaching this week based on at-risk accounts and deals?',
        'Summarize team quota coverage and the one metric I should watch on this dashboard.',
        'Draft a concise weekly team update I can send to leadership from this view.',
      ];
    }
    if (salesPersona === 'ae') {
      return [
        'Which accounts in my patch need attention first this week, and why?',
        'Summarize pipeline health and the one deal I should focus on today.',
        'What is likely stale or wrong in my dashboard view?',
      ];
    }
    return [
      'What should I prioritize on my dashboard today?',
      'Summarize pipeline health and the one metric to watch.',
      'What is likely stale or wrong in my workspace view?',
    ];
  }

  return [...DEFAULT_STARTERS];
}

export function getAssistantFollowUpChips(ctx?: AssistantRouteContext | null): string[] {
  const p = normPath(ctx);

  if (p.includes('/sales-hub')) {
    return [
      'What should I ship first in this hub?',
      'Tighten messaging for our ICP in 5 bullets.',
      'What would a skeptical buyer still question?',
    ];
  }

  if (
    (p === '/crm/accounts' ||
      p === '/crm/territory' ||
      (p.startsWith('/crm/accounts') && !/\/crm\/accounts\/[^/]+/.test(p))) &&
    p.includes('scope=team')
  ) {
    return [
      'Which rep should I coach first on account risk?',
      'Draft a 3-bullet team patch plan from this view.',
      'What team signal am I ignoring in my current filter?',
    ];
  }

  if (
    p === '/crm/accounts' ||
    p === '/crm/territory' ||
    (p.startsWith('/crm/accounts') && !/\/crm\/accounts\/[^/]+/.test(p))
  ) {
    return [
      'Which single account should I call first today?',
      'Draft a 3-bullet patch plan for my manager from this view.',
      'What signal am I ignoring in my current focus filter?',
    ];
  }

  if (p === '/crm' || p.startsWith('/crm/')) {
    return [
      'What is one metric I should not trust at face value here?',
      'Who should I sync with this week based on this CRM context?',
      'What is the fastest win if I only have 30 minutes?',
    ];
  }

  if (p.includes('/deals')) {
    return [
      'What would change my forecast confidence?',
      'Give a MEDDPICC-style gap check.',
      'Draft a one-paragraph exec summary.',
    ];
  }

  if (p.includes('/calendar')) {
    return [
      'What prep am I underestimating?',
      'Suggest questions to ask in the first 10 minutes.',
      'What follow-ups should I calendar immediately?',
    ];
  }

  if (p.includes('/messaging')) {
    return [
      'Make this shorter and clearer.',
      'What is the single ask in this thread?',
      'What tone mismatch should I fix?',
    ];
  }

  if (p.includes('/territory') || (p.includes('/contacts') && !p.startsWith('/crm'))) {
    return [
      'Who is the next best account to touch?',
      'What research should I do in 10 minutes?',
      'What is the risk if I wait a week?',
    ];
  }

  return [...DEFAULT_FOLLOW_UPS];
}
